import { Box, Grid, Stack, Typography } from "@mui/material";
import type { InvoiceEntity } from "../../../api/types";
import { useRegistry } from "../../../context/RegistryContext";
import { useAttachments } from "../../../hooks/useAttachments";
import { invoiceSender, SENDER_LABEL } from "../../../logic/invoice-sender";
import { formatInvoiceAmount } from "../../../logic/invoice-amount";
import { SenderHeading } from "../../SenderHeading";
import { DateDisplay } from "../../DateDisplay";
import { CopyableText } from "../../CopyableText";
import AttachmentPreview from "../../AttachmentPreview";
import { InvoiceField } from "./InvoiceField";

export type InvoiceDetailsProps = {
  /** The stored invoice — from `/lists/invoices`, not the registry, whose
   * projection leaves out the amounts, KID and pay-to account. */
  invoice: InvoiceEntity;
};

/** An invoice's own fields, read-only, for its view dialog. */
export const InvoiceDetails = ({ invoice }: InvoiceDetailsProps) => {
  // Hidden senders included: hiding keeps a payee out of pickers, not off the
  // invoices it sent.
  const { withHidden } = useRegistry();
  const { data: attachmentsList } = useAttachments();
  const sender = invoiceSender(invoice, withHidden);
  const amount = formatInvoiceAmount(invoice);
  const attachments = (invoice.attachments ?? []).flatMap((id) => {
    const found = attachmentsList?.find((a) => a.id === id);
    return found ? [found] : [];
  });

  const senderSubtitle = sender
    ? `${SENDER_LABEL[sender.kind]}${sender.entity?.hidden ? " (hidden)" : ""}`
    : undefined;

  return (
    <Grid container spacing={2}>
      <InvoiceField
        label="From"
        value={
          <SenderHeading
            invoice={invoice}
            size="md"
            subtitle={senderSubtitle}
          />
        }
      />
      <InvoiceField
        label="Amount"
        value={amount && <Typography variant="body1">{amount}</Typography>}
        empty="Not set"
      />
      <InvoiceField
        label="Received"
        value={<DateDisplay date={invoice.dateReceived} />}
      />
      <InvoiceField
        label="Due"
        value={<DateDisplay date={invoice.dueDate} />}
      />
      <InvoiceField
        label="KID"
        value={invoice.kid && <CopyableText text={invoice.kid} />}
      />
      <InvoiceField
        label="Pay-to account"
        value={
          invoice.payToAccount && <CopyableText text={invoice.payToAccount} />
        }
      />
      <InvoiceField
        label="Description"
        size={12}
        value={
          invoice.description && (
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {invoice.description}
            </Typography>
          )
        }
      />
      <InvoiceField
        label="Attachments"
        size={12}
        value={
          attachments.length > 0 && (
            <Stack spacing={1}>
              {attachments.map((attachment) => (
                <Stack
                  key={attachment.id}
                  direction="row"
                  sx={{ alignItems: "center", minWidth: 0 }}
                >
                  <AttachmentPreview attachment={attachment} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      title={attachment.filename}
                    >
                      {attachment.filename}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          )
        }
      />
    </Grid>
  );
};
